import {
  DashboardSpreadsheet,
  DataImport,
  DataImportRun,
  DataImportStatusLevel,
  DataSourceType,
} from "@coeff/api";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  Breadcrumb,
  BreadcrumbItem,
  DEPRECATED_Button,
  Card as _Card,
  CardHeader as _CardHeader,
  ConfirmBox,
  ErrorMessage,
  ImportTypeIcon,
  NewWindowNew,
  Table,
  Tooltip,
  WarningMessage,
  LoaderWithPerfTimings,
  Typography,
  Card,
  CardHeader,
  Settings,
} from "../../../../components";
import { ImportDetailsSidebarContent } from "../../../../components/ImportDetailsSidebarContent";
import { StatusTag } from "../../../../components/StatusTag";
import { COLORS } from "../../../../constants";
import { capitalize, getRefreshedDate, useTrack } from "../../../../utils";
import { Header } from "../../components";
import { useDashboardContext } from "../../Dashboard";

const Wrap = styled.div`
  .title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 12px;

    .infoText {
      float: right;
      color: ${COLORS.black45};
      font-size: 12px;
      font-weight: normal;
    }
  }
`;

const Sidebar = styled.div`
  width: 300px;
  position: absolute;
  right: 0px;
  top: 0px;
  bottom: 0px;
  border-left: 2px solid ${COLORS.black15};
  background: white;
  font-size: 13px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  .subText {
    font-size: 12px;
    color: ${COLORS.black45};
    margin: 4px 0px;
  }
`;

export const ImportDetailsPage: React.FC<{ dataImports: DataImport[] }> = ({ dataImports }) => {
  const track = useTrack();

  const { getDataImportRunHistory, spreadsheets } = useDashboardContext();

  const [dataImportRuns, setDataImportRuns] = useState<DataImportRun[]>([]);

  const [showFailedRunError, setShowFailedRunError] = useState<{
    status_level?: DataImportStatusLevel;
    title?: string;
    message?: string;
    messageHtml?: string;
  } | null>(null);

  const [loading, setLoading] = useState<boolean>(true);

  const params = useParams<{ importId: string; ssId: string }>();

  const history = useHistory();

  const dataImport = dataImports.find(di => di.data_import_id === params.importId);

  const spreadsheet = spreadsheets?.find(
    ss => ss.google_sheets_file_id === params.ssId
  ) as DashboardSpreadsheet;

  useEffect(() => {
    if (dataImport && spreadsheet) {
      track("workspace_import_viewed", {
        data_import_id: dataImport?.data_import_id,
        ss_id: spreadsheet.google_sheets_file_id,
        ss_name: spreadsheet.google_sheets_file_name,
      });
    }
  }, [dataImport, spreadsheet]);

  useEffect(() => {
    const getRuns = async () => {
      if (dataImport) {
        setLoading(true);
        const { data_import_runs } = await getDataImportRunHistory(
          dataImport.data_import_id ?? "",
          1
        );

        setDataImportRuns(data_import_runs);
        setLoading(false);
      }
      if (!dataImport && dataImports.length > 0) {
        history.push(`/dashboard/spreadsheets/${spreadsheet.google_sheets_file_id}`);
      }
    };
    getRuns();
  }, [dataImport, spreadsheets, dataImports]);

  const onOpenSheetClick = () => {
    track("workspace_open_sheet_clicked", {
      event_from: "import_details",
      data_import_id: dataImport?.data_import_id,
      import_data_source: dataImport?.data_source_type,
      ss_id: spreadsheet.google_sheets_file_id,
      ss_name: spreadsheet.google_sheets_file_name,
    });
  };

  const onDataImportRunPageNumberChange = async (page, pageSize) => {
    const isCurrentlyOnLastPage = page === Math.ceil(dataImportRuns.length / pageSize);

    if (isCurrentlyOnLastPage) {
      const { data_import_runs } = await getDataImportRunHistory(
        dataImport?.data_import_id ?? "",
        dataImportRuns.length + 1
      );

      setDataImportRuns([...dataImportRuns, ...data_import_runs]);
    }
  };

  const columns: ColumnsType<DataImportRun> = [
    {
      title: "Status",
      dataIndex: "status_type",
      key: "status_type",
      render: (text, record) => (
        <>
          <StatusTag status={record.data_import_status?.status_level || text} />

          {["error", "warning"].includes(record.data_import_status?.status_level ?? "") && (
            <a
              style={{ marginLeft: 4 }}
              href="javascript:void(0)"
              onClick={() => {
                setShowFailedRunError({
                  status_level: record.data_import_status?.status_level,
                  title: record.data_import_status?.title,
                  message: record.data_import_status?.message,
                  messageHtml: record.data_import_status?.message_html,
                });
              }}
            >
              View Details
            </a>
          )}
        </>
      ),
    },
    {
      title: "Time",
      dataIndex: "run_complete_dt",
      key: "run_complete_dt",
      render: d => {
        if (d) {
          const date = moment(d);
          return date.format("MMM DD, YYYY hh:mmA");
        } else {
          return "";
        }
      },
    },
    {
      title: "Refresh Trigger",
      dataIndex: "trigger_type",
      key: "trigger_type",
      render: (triggerType, record) => {
        return `${capitalize(triggerType)} ${
          triggerType === "manual" ? "(" + record.user_display_name + ")" : ""
        }`;
      },
    },
    {
      title: (
        <div>
          <Tooltip title="Includes header row">
            <span style={{ borderBottom: "1px dotted " + COLORS.black65 }}>Rows</span>
          </Tooltip>
        </div>
      ),
      dataIndex: "num_rows",
      key: "num_rows",
      className: "center",
    },
    {
      title: "Columns",
      dataIndex: "num_columns",
      key: "num_columns",
      className: "center",
    },

    ...(dataImport?.data_source_type === DataSourceType.Gmail
      ? [
          {
            title: "Emails Processed",
            dataIndex: ["connector_run_metadata", "total_num_mails"],
            key: "total_num_mails",
            className: "center",
          },
          {
            title: "Notes",
            dataIndex: ["connector_run_metadata", "gmail_run_notes"],
            key: "gmail_run_notes",
            render: (gmailRunNotes?: Array<string>) => {
              return (
                <Typography isEllipsedContentTooltip lineClamp={1} style={{ maxWidth: 200 }}>
                  {gmailRunNotes?.join(", ")}
                </Typography>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div style={{ maxWidth: "calc(100% - 300px)", padding: "20px 30px" }}>
      {loading && (
        <div style={{ margin: "5rem auto 2rem", textAlign: "center" }}>
          <LoaderWithPerfTimings name="ImportDetails" size="large" />
        </div>
      )}

      {!loading && (
        <div>
          <Wrap>
            <Header>
              <div>
                {dataImport?.data_source_type && (
                  <ImportTypeIcon type={dataImport?.data_source_type} />
                )}
              </div>

              <div className="details">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link to={{ pathname: `/dashboard/spreadsheets` }}>Spreadsheets</Link>
                  </BreadcrumbItem>

                  <BreadcrumbItem>
                    <Link
                      to={{
                        pathname: `/dashboard/spreadsheets/${spreadsheet.google_sheets_file_id}`,
                        search: window.location.search,
                      }}
                    >
                      {spreadsheet.google_sheets_file_name}
                    </Link>
                  </BreadcrumbItem>
                  <div style={{ visibility: "hidden" }}></div>
                </Breadcrumb>

                <h1>{dataImport?.data_import_name}</h1>

                <span className="refreshDate">{`Refreshed ${getRefreshedDate(
                  moment.utc(dataImport?.last_run_dt).local()
                )}`}</span>
              </div>

              {spreadsheet?.spreadsheet_url && (
                <div>
                  <DEPRECATED_Button
                    type="primary"
                    href={dataImport?.data_import_url}
                    style={{ height: "40px", lineHeight: "40px" }}
                    target="_blank"
                    onClick={onOpenSheetClick}
                  >
                    Open Sheet{" "}
                    <NewWindowNew style={{ position: "relative", top: "2px", marginLeft: "6px" }} />
                  </DEPRECATED_Button>
                </div>
              )}
            </Header>

            <div style={{ marginLeft: "46px" }}>
              <div className="title">
                Run History
                <span className="infoText">Displaying last 30 days</span>
              </div>

              <Table
                dataSource={dataImportRuns}
                locale={{ emptyText: "No import runs in the last 30 days" }}
                pagination={
                  dataImportRuns.length > 25
                    ? {
                        position: ["bottomCenter"],
                        size: "small",
                        pageSize: 25,
                        showSizeChanger: false,
                        onChange: onDataImportRunPageNumberChange,
                      }
                    : false
                }
                columns={columns}
              />
            </div>
          </Wrap>
        </div>
      )}

      {dataImport ? (
        <Sidebar>
          <ImportDetailsSidebarContent dataImport={dataImport} spreadsheet={spreadsheet} />
        </Sidebar>
      ) : null}

      <ConfirmBox
        visible={!!showFailedRunError}
        closable={true}
        showCancel={false}
        onOk={() => setShowFailedRunError(null)}
        onCancel={() => setShowFailedRunError(null)}
        okText="Got it"
        title={showFailedRunError?.status_level === "warning" ? "Warning" : "Import Failed"}
        width={400}
      >
        <>
          {showFailedRunError?.status_level === "warning" && (
            <WarningMessage {...showFailedRunError} />
          )}
          {showFailedRunError?.status_level === "error" && <ErrorMessage {...showFailedRunError} />}
        </>
      </ConfirmBox>
    </div>
  );
};
